import { ApiResponse } from './../interfaces/ApiResponses';
import ApiService from '@/core/services/ApiService';
import { AxiosResponse } from 'axios';
import {
  AuthenticationDataResponse,
  INguoiDungCreateRequest,
  INguoiDungDetailResponse,
  INguoiDungPhanQuyenPayload,
  INguoiDungUpdatePasswordPayload,
  INguoiDungUpdatePayload,
  IUserResponse,
  IUserResponseWithRoleBase,
  PaginationResponseBase,
} from '@/core/interfaces/ApiResponses';
import { apiServiceInstance } from '@/core/helpers/utils';
import { extractFilterDateRange } from '@/core/helpers/filter-date-range';
import { extractWorkingYears } from '@/core/helpers/working-years';
import store from '@/store';
import { Mutations } from '@/store/enums/StoreEnums';

export class UserService {
  static login(
    phone: string,
    password: string
  ): Promise<AxiosResponse<AuthenticationDataResponse>> {
    return ApiService.vueInstance.axios.post('/api/v1/auth/login', {
      so_dien_thoai: phone,
      mat_khau: password,
    });
  }

  static getCurrentUserData(): Promise<
    AxiosResponse<{
      data: IUserResponse;
    }>
  > {
    return apiServiceInstance().vueInstance.axios.post('/api/v1/auth/me');
  }

  /** Load /auth/me and sync user + filter_date_range into the auth store. */
  static async syncCurrentUser(): Promise<IUserResponse | null> {
    const response = await UserService.getCurrentUserData();
    const payload = response.data?.data;
    if (!payload) {
      return null;
    }

    store.commit(Mutations.SET_USER, payload);
    const year = store.getters.getSelectedYear as string | null;
    const range = extractFilterDateRange(
      payload,
      year ? Number(year) : undefined
    );
    if (range) {
      store.commit(Mutations.SET_FILTER_DATE_RANGE, range);
    }

    const workingYears = extractWorkingYears(payload);
    if (workingYears?.length) {
      store.commit(Mutations.SET_WORKING_YEARS, workingYears);
    }

    return payload;
  }

  static list(
    page = 1,
    perPage = 15,
    search = '',
    idDonVi?: number
  ): Promise<{
    data: ApiResponse<PaginationResponseBase<IUserResponseWithRoleBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/nguoi-dung/danh-sach',
      {
        params: {
          page,
          per_page: perPage,
          ...(search && { s: search }),
          ...(idDonVi && { id_don_vi: idDonVi }),
        },
      }
    );
  }

  static get(id: number): Promise<{
    data: ApiResponse<INguoiDungDetailResponse>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/nguoi-dung/chi-tiet/${id}`
    );
  }

  static phanQuyen(
    id: number,
    payload: INguoiDungPhanQuyenPayload
  ): Promise<{
    data: ApiResponse<INguoiDungDetailResponse>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/nguoi-dung/phan-quyen/${id}`,
      payload
    );
  }

  static create(payload: INguoiDungCreateRequest): Promise<{
    data: ApiResponse<INguoiDungDetailResponse>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/nguoi-dung/tao-moi',
      payload
    );
  }

  static update(
    id: number,
    payload: INguoiDungUpdatePayload
  ): Promise<{
    data: ApiResponse<IUserResponseWithRoleBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/nguoi-dung/cap-nhat/${id}`,
      { nguoiDungInstance: payload }
    );
  }

  static updatePassword(
    id: number,
    payload: INguoiDungUpdatePasswordPayload
  ): Promise<{
    data: ApiResponse<IUserResponseWithRoleBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/nguoi-dung/doi-mk/${id}`,
      { nguoiDungInstance: payload }
    );
  }

  static remove(id: number): Promise<{
    data: ApiResponse<{ id: number }>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/nguoi-dung/xoa/${id}`
    );
  }

  static refreshToken(): Promise<AxiosResponse<AuthenticationDataResponse>> {
    return apiServiceInstance().vueInstance.axios.post('/api/v1/auth/refresh');
  }

  static logout(): Promise<AxiosResponse> {
    return apiServiceInstance().vueInstance.axios.post('/api/v1/auth/logout');
  }
}
